import { createPortal } from "react-dom";
import { ExtendPadding, LayerProps, Point} from "../../../types/imageStudio/images";
import { SengiColors } from "../../../utils/SengiColors";
import { ensureOverlayInBounds, useDragging } from "../../../utils/images/overlayFunctions";
import {  getLocalCornerForVisualCorner, getOutwardDragAmountPx, getVisualToLocalSideMap, OverlayCorner, OverlayPortalPropsImageStudio, OverlaySide } from "../../../types/imageStudio/overlay";
import { ContextMenu } from "./ContextMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate } from "@fortawesome/free-solid-svg-icons";
import { normalizeRotation } from "../../../utils/images/editFunctions";
import {canvasDeltaToLayerLocalDelta, getLayerCssTransform, getLayerOverlayCounterTransform, getPreviewHardnessLayers, hexToRgbaCss, normalizeRect } from "../../../utils/images/layerFunctions";
import React, { useState } from "react";
import { getDrawingCursorValue } from "../../../utils/DrawingCursorValue";
import { ContextProps } from "../../../types/imageStudio/contextMenu";
import { ExpandPaddingPreview } from "./Previews";
import { useBootstrapTheme } from "../../../contexts/BootstrapThemeContext";
import { useTranslation } from "react-i18next";

export const OverlayPortal = ({
    canvasDisplayRect,
    canvasRef,
    canvasWrapperRef,
    overlayRect,
    activeOverlayBaseRect,
    activeToolkitTool="pointer",
    imageLayer,
    selectedLayer,
    selectedLayerIDs=[],
    marqueeSelectionPreview,
    activeLayerEditId=null,
    layerAreaSelection,
    selectionPreview,
    overlayTarget="image",
    rotateMode=false,
    rotationDraft=0,
    cropMode=false,
    isPickingColor=false,
    drawingCursorPreview = null,
    drawingStrokePreview,
    selectedDrawingTool = "",
    shapePreview,
    editingTextLayerId = null,
    editingTextValue = "",
    canvasMoveOrResizeIsActive=false,
    selectedImage,
    hoverImagePreview,
    onResolveGalleryImageFile,
    onDropGalleryImageOnCanvas,
    onDropGalleryImageOnSelectedImageLayer,
    onPickSurfaceColor,
    onStartTextEdit,
    onChangeEditingText,
    onCommitTextEdit,
    setOverlayRect,
    updatePointerSelection,
    beginLayerTransform,
    beginSelectionDrag,
    beginDrawingStroke,
    onIsRotating,
    closeOverlays,
    handleCropSave,
    handleRotateSave,
    setContextMenu,
    onSelectedLayerChange,
    onSelectedLayerIDsChange,
    beginPointerMarqueeSelection,
    setLayerAreaSelection,
    onDropImageOnCanvas,
    onDropImageOnSelectedImageLayer,
    onCancelExtendImage,
    onChangeExtendPadding,
    onSaveExtendImage,
    openExtandImage
}: OverlayPortalPropsImageStudio) => {
    const [dragStateRef, beginDrag, endDrag, onDragMove] = useDragging({overlayRect, activeOverlayBaseRect,setOverlayRect});
    const { effectiveTheme } = useBootstrapTheme();
    const { t } = useTranslation("common");
    const [dragTarget, setDragTarget] = useState<null | "canvas" | "selected-image-layer">(null);

    const selectionStroke = SengiColors.sng_lavendel;
    const selectionFill = "rgba(147,129,255,0.16)";
    const selectedLayerBorder = `2px solid ${SengiColors.sng_lavendel}`;
    const selectedLayerGlow = `0 0 0 1px ${SengiColors.sng_lavendel}, 0 0 14px rgba(147,129,255,0.28)`;
    const dragHighlightBorder = "3px solid #00D1FF";
    const dragHighlightGlow = "0 0 0 1px rgba(0,209,255,0.35), 0 0 16px rgba(0,209,255,0.45)";
    const overlayStyle = activeOverlayBaseRect
        ? {
            position: "absolute" as const,
            left: activeOverlayBaseRect.left + overlayRect.x * activeOverlayBaseRect.width,
            top: activeOverlayBaseRect.top + overlayRect.y * activeOverlayBaseRect.height,
            width: overlayRect.w * activeOverlayBaseRect.width,
            height: overlayRect.h * activeOverlayBaseRect.height,
        }
        : {};
    const canvasBoundaryRef = React.useRef<HTMLDivElement | null>(null);
    const canvasBoundaryFrameRef = React.useRef<number | null>(null);
    const canvas = canvasRef?.current as HTMLCanvasElement | null;
    const scaleX = canvas && canvasDisplayRect ? canvasDisplayRect.width / canvas.width : 1;
    const scaleY = canvas && canvasDisplayRect ? canvasDisplayRect.height / canvas.height : 1;

    const hasSizePreviewTool =
        selectedDrawingTool === "brush" ||
        selectedDrawingTool === "eraser";

    const isFillCursorTool = selectedDrawingTool === "fill";
    const previewScreenX = drawingCursorPreview
        ? canvasDisplayRect!.left + drawingCursorPreview.x * scaleX
        : 0;

    const previewScreenY = drawingCursorPreview
        ? canvasDisplayRect!.top + drawingCursorPreview.y * scaleY
        : 0;
    const outsideCanvasMaskColor = effectiveTheme === "dark"
        ? "rgba(0, 0, 0, 0.28)"
        : `rgba(1, 53, 150, 0.10)`

    const previewWidth = drawingCursorPreview ? drawingCursorPreview.size * scaleX : 0;
    const previewHeight = drawingCursorPreview ? drawingCursorPreview.size * scaleY : 0;

    const fillIndicatorSize = 4;

    const drawingCursorDisplayStyle =
        drawingCursorPreview && canvasDisplayRect
            ? hasSizePreviewTool
                ? {
                    position: "absolute" as const,
                    left: previewScreenX - previewWidth / 2,
                    top: previewScreenY - previewHeight / 2,
                    width: previewWidth,
                    height: previewHeight,
                    borderRadius: "50%",
                    border: "1px solid #6c757d",
                    backgroundColor: "transparent",
                    pointerEvents: "none" as const,
                    zIndex: 21000
                }
                : isFillCursorTool
                    ? {
                        position: "absolute" as const,
                        left: previewScreenX - fillIndicatorSize / 2,
                        top: previewScreenY - fillIndicatorSize / 2,
                        width: fillIndicatorSize,
                        height: fillIndicatorSize,
                        borderRadius: "50%",
                        border: "1px solid #6c757d",
                        backgroundColor: "#ffffff",
                        pointerEvents: "none" as const,
                        zIndex: 21000
                    }
                    : null
            : null;
    const isSelectionTool = activeToolkitTool !== null && ["select-rect", "select-oval", "select-free"].includes(activeToolkitTool);
    const visibleSelectedLayerIDs = selectedLayerIDs.filter(id => imageLayer.some(layer => layer.layerID === id && !layer.hidden));

    const hasSingleSelectedLayer = visibleSelectedLayerIDs.length === 1;
    const selectedAreaTargetLayerId = hasSingleSelectedLayer
            ? visibleSelectedLayerIDs[0]
            : null;
    const visibleSelectedLayer = selectedLayer && !selectedLayer.hidden && visibleSelectedLayerIDs.includes(selectedLayer.layerID)
        ? selectedLayer
        : null;
    const isDrawingActionTool =
        selectedDrawingTool === "brush" ||
        selectedDrawingTool === "eraser" ||
        selectedDrawingTool === "fill" ||
        selectedDrawingTool.startsWith("shape-");
    
    const showCanvasBorder =
        selectedImage &&
        !hoverImagePreview &&
        !rotateMode &&
        !canvasMoveOrResizeIsActive &&
        !openExtandImage.active;
    const drawingCursorHotspot =
        selectedDrawingTool === "brush" ||
        selectedDrawingTool === "eraser" ||
        selectedDrawingTool === "fill"
            ? "2 49, crosshair"
            : "2 20, crosshair";

    const renderExtendOverlay = () => {
        if (!openExtandImage.active || !canvasDisplayRect || hoverImagePreview) return null;

        const padding = openExtandImage.padding;
        const layerTransformSource =
            openExtandImage.target === "layer" && visibleSelectedLayer
                ? visibleSelectedLayer
                : null;

        const visualToLocalSide = getVisualToLocalSideMap(layerTransformSource);

        const localPadding = layerTransformSource
            ? {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                [visualToLocalSide.top]: padding.top,
                [visualToLocalSide.right]: padding.right,
                [visualToLocalSide.bottom]: padding.bottom,
                [visualToLocalSide.left]: padding.left
            }
            : padding;
        const targetRect =
            openExtandImage.target === "layer" && visibleSelectedLayer
                ? {
                    left: canvasDisplayRect.left + visibleSelectedLayer.position.x * scaleX,
                    top: canvasDisplayRect.top + visibleSelectedLayer.position.y * scaleY,
                    width: visibleSelectedLayer.width * scaleX,
                    height: visibleSelectedLayer.height * scaleY,
                    rotation: visibleSelectedLayer.rotation ?? 0,
                    flipHorizontal: visibleSelectedLayer.flipHorizontal ?? false,
                    flipVertical: visibleSelectedLayer.flipVertical ?? false
                }
                : {
                    left: canvasDisplayRect.left,
                    top: canvasDisplayRect.top,
                    width: canvasDisplayRect.width,
                    height: canvasDisplayRect.height,
                    rotation: 0,
                    flipHorizontal: false,
                    flipVertical: false
                };

        const leftPx = localPadding.left * scaleX;
        const rightPx = localPadding.right * scaleX;
        const topPx = localPadding.top * scaleY;
        const bottomPx = localPadding.bottom * scaleY;

        const outerWidth = targetRect.width + leftPx + rightPx;
        const outerHeight = targetRect.height + topPx + bottomPx;

        const targetTransform = [
            targetRect.flipHorizontal ? "scaleX(-1)" : "",
            targetRect.flipVertical ? "scaleY(-1)" : "",
            targetRect.rotation ? `rotate(${targetRect.rotation}deg)` : ""
        ].filter(Boolean).join(" ");

        const startDrag = (
            e: React.MouseEvent,
            edge: "top" | "right" | "bottom" | "left" | "nw" | "ne" | "sw" | "se"
        ) => {
            e.preventDefault();
            e.stopPropagation();

            const startX = e.clientX;
            const startY = e.clientY;
            const startPadding = { ...padding };

            const onMove = (ev: MouseEvent) => {
                const nextPadding = { ...startPadding };

                const amountFor = (side: OverlaySide) =>
                    Math.round(
                        getOutwardDragAmountPx(
                            ev,
                            startX,
                            startY,
                            layerTransformSource,
                            side
                        ) / (side === "left" || side === "right" ? scaleX : scaleY)
                    );

                if (edge.includes("n") || edge === "top") {
                    nextPadding.top = Math.max(0, startPadding.top + amountFor("top"));
                }

                if (edge.includes("e") || edge === "right") {
                    nextPadding.right = Math.max(0, startPadding.right + amountFor("right"));
                }

                if (edge.includes("s") || edge === "bottom") {
                    nextPadding.bottom = Math.max(0, startPadding.bottom + amountFor("bottom"));
                }

                if (edge.includes("w") || edge === "left") {
                    nextPadding.left = Math.max(0, startPadding.left + amountFor("left"));
                }

                onChangeExtendPadding(nextPadding, {
                    clientX: ev.clientX,
                    clientY: ev.clientY
                });
            };

            const onUp = () => {
                window.removeEventListener("mousemove", onMove);
                window.removeEventListener("mouseup", onUp);
            };

            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
        };

        const expandCounterTransform = [
            targetRect.rotation ? `rotate(${-targetRect.rotation}deg)` : "",
            targetRect.flipVertical ? "scaleY(-1)" : "",
            targetRect.flipHorizontal ? "scaleX(-1)" : ""
        ].filter(Boolean).join(" ");

        return (
            <div style={{ position: "absolute", inset: 0, zIndex: 26000, pointerEvents: "none" }}>
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: canvasWrapperRef.current?.scrollWidth ?? "100%",
                        height: canvasWrapperRef.current?.scrollHeight ?? "100%",
                        backgroundImage: "radial-gradient(circle, rgba(147,129,255,0.55) 1px, transparent 1px)",
                        backgroundSize: `${50 * scaleX}px ${50 * scaleY}px`,
                        backgroundPosition: `${targetRect.left}px ${targetRect.top}px`,
                        pointerEvents: "none"
                    }}
                />
                <div
                    className="position-fixed"
                    style={{
                        top: "1.2em",
                        right: "1.5em",
                        pointerEvents: "none",
                        zIndex: 10000
                    }}
                >
                    <div className="row m-0 p-0" style={{ pointerEvents: "auto" }}>
                        <div className="btn-group shadow m-0 p-0">
                            <button
                                className={`btn btn-sm ${effectiveTheme=== 'dark' ? 'btn-light' : 'btn-sengi'}`}
                                type="button"
                                title={t('actions.save')}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onSaveExtendImage();
                                }}
                            >
                                {t('actions.save')}
                            </button>

                            <button
                                className={`btn btn-sm ${effectiveTheme=== 'dark' ? 'btn-outline-light' : 'btn-outline-sengi'}`}
                                type="button"
                                title={t('actions.cancel')}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onCancelExtendImage();
                                }}
                            >
                                {t('actions.cancel')}
                            </button>
                        </div>
                    </div>
                    <div className="row mt-2">
                        <ExpandPaddingPreview
                            padding={padding}
                            targetWidth={targetRect.width / scaleX}
                            targetHeight={targetRect.height / scaleY}
                            onChangePadding={onChangeExtendPadding}
                        />
                    </div>
                </div>

                <div
                    style={{
                        position: "absolute",
                        left: targetRect.left,
                        top: targetRect.top,
                        width: targetRect.width,
                        height: targetRect.height,
                        transform: targetTransform || undefined,
                        transformOrigin: "center center",
                        pointerEvents: "none"
                    }}
                >
                    <div
                        style={{
                            position: "absolute",
                            left: -leftPx,
                            top: -topPx,
                            width: outerWidth,
                            height: outerHeight,
                            border: `2px dashed ${SengiColors.sng_lavendel}`,
                            boxShadow: "0 0 0 9999px rgba(0,0,0,0.18)",
                            pointerEvents: "auto"
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                left: leftPx,
                                top: topPx,
                                width: targetRect.width,
                                height: targetRect.height
                            }}
                        />

                        {(["nw", "ne", "sw", "se"] as const).map(pos => {
                            const localCorner = getLocalCornerForVisualCorner(layerTransformSource, pos);
                        
                            return (    
                                <button
                                    key={pos}
                                    onMouseDown={(e) => startDrag(e, pos)}
                                    style={{
                                        position: "absolute",
                                        width: 16,
                                        height: 16,
                                        borderRadius: "50%",
                                        border: `2px solid ${SengiColors.sng_lavendel}`,
                                        background: "#fff",
                                        transform: expandCounterTransform  || undefined,
                                        transformOrigin: "center center",
                                        left: localCorner.includes("w") ? -8 : outerWidth - 8,
                                        top: localCorner.includes("n") ? -8 : outerHeight - 8,
                                        cursor: `${pos}-resize`
                                    }}
                                />
                        )})}
                    </div>
                </div>
            </div>
        );
    };
    
    const getLayerDisplayStyle = (layer: LayerProps) => {
        const canvas = canvasRef?.current as HTMLCanvasElement | null;
        if (!canvas || !canvasDisplayRect) return null;

        const scaleX = canvasDisplayRect.width / canvas.width;
        const scaleY = canvasDisplayRect.height / canvas.height;

        return {
            left: canvasDisplayRect.left + layer.position.x * scaleX,
            top: canvasDisplayRect.top + layer.position.y * scaleY,
            width: layer.width * scaleX,
            height: layer.height * scaleY
        };
    };

    const getDisplayBorderRadiusPx = (
        value: number | undefined,
        unit: "px" | "%" | undefined,
        displayWidth: number,
        displayHeight: number,
        displayScale: number
    ) => {
        const safeValue = Math.max(0, Number(value) || 0);

        if (unit === "%") {
            return Math.min(displayWidth, displayHeight) * Math.min(50, safeValue) / 100;
        }

        return Math.min(safeValue * displayScale, displayWidth / 2, displayHeight / 2);
    };

    const updateCanvasBoundary = React.useCallback(() => {
        const canvasElement = canvasRef?.current;
        const wrapperElement = canvasWrapperRef.current;
        const boundaryElement = canvasBoundaryRef.current;

        if (!canvasElement || !wrapperElement || !boundaryElement) {
            return;
        }

        const canvasRect = canvasElement.getBoundingClientRect();
        const wrapperRect = wrapperElement.getBoundingClientRect();

        const borderWidth = 2;

        const left =
            canvasRect.left -
            wrapperRect.left +
            wrapperElement.scrollLeft -
            borderWidth;

        const top =
            canvasRect.top -
            wrapperRect.top +
            wrapperElement.scrollTop -
            borderWidth;

        const width = canvasRect.width + borderWidth * 2;
        const height = canvasRect.height + borderWidth * 2;

        boundaryElement.style.transform =
            `translate3d(${left}px, ${top}px, 0)`;

        boundaryElement.style.width = `${width}px`;
        boundaryElement.style.height = `${height}px`;
    }, [canvasRef, canvasWrapperRef]);

    const activeEditedLayer =
        overlayTarget === "layer" && activeLayerEditId != null
            ? imageLayer.find((l) => l.layerID === activeLayerEditId) ?? null
            : null;

    const cropLayerTransform =
        activeEditedLayer?.rotation
            ? `rotate(${activeEditedLayer.rotation}deg)`
            : "";

    const cropOverlayCounterTransform =
        activeEditedLayer?.rotation
            ? `rotate(${-activeEditedLayer.rotation}deg)`
            : "";

    const beginCropDrag = (
        e: React.MouseEvent,
        mode: "move" | "nw" | "ne" | "sw" | "se"
    ) => {
        if (overlayTarget !== "layer" || !activeEditedLayer || !activeOverlayBaseRect) {
            beginDrag(e, mode);
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startY = e.clientY;
        const startRect = { ...overlayRect };
        
        const onMove = (ev: MouseEvent) => {
            let r = { ...startRect };

            const layerTransformSource = activeEditedLayer;

            const visualToLocalSide = getVisualToLocalSideMap(layerTransformSource);

            const amountFor = (visualSide: OverlaySide) => {
                const px = getOutwardDragAmountPx(
                    ev,
                    startX,
                    startY,
                    layerTransformSource,
                    visualSide
                );

                const localSide = visualToLocalSide[visualSide];

                return px / (
                    localSide === "left" || localSide === "right"
                        ? activeOverlayBaseRect.width
                        : activeOverlayBaseRect.height
                );
            };

            const applySide = (visualSide: OverlaySide, amount: number) => {
                const localSide = visualToLocalSide[visualSide];

                const right = r.x + r.w;
                const bottom = r.y + r.h;

                if (localSide === "top") {
                    r.y -= amount;
                    r.h = bottom - r.y;
                }

                if (localSide === "bottom") {
                    r.h += amount;
                }

                if (localSide === "left") {
                    r.x -= amount;
                    r.w = right - r.x;
                }

                if (localSide === "right") {
                    r.w += amount;
                }
            };

            if (mode === "move") {
                const rightAmount = amountFor("right");
                const bottomAmount = amountFor("bottom");

                const localRight = visualToLocalSide.right;
                const localBottom = visualToLocalSide.bottom;

                if (localRight === "right") r.x += rightAmount;
                if (localRight === "left") r.x -= rightAmount;
                if (localRight === "top") r.y -= rightAmount;
                if (localRight === "bottom") r.y += rightAmount;

                if (localBottom === "right") r.x += bottomAmount;
                if (localBottom === "left") r.x -= bottomAmount;
                if (localBottom === "top") r.y -= bottomAmount;
                if (localBottom === "bottom") r.y += bottomAmount;
            } else {
                if (mode.includes("n")) applySide("top", amountFor("top"));
                if (mode.includes("e")) applySide("right", amountFor("right"));
                if (mode.includes("s")) applySide("bottom", amountFor("bottom"));
                if (mode.includes("w")) applySide("left", amountFor("left"));
            }

            r = ensureOverlayInBounds(r);
            setOverlayRect(r);
        };

        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };
    const scheduleCanvasBoundaryUpdate = React.useCallback(() => {
        if (canvasBoundaryFrameRef.current != null) {
            return;
        }

        canvasBoundaryFrameRef.current = requestAnimationFrame(() => {
            canvasBoundaryFrameRef.current = null;
            updateCanvasBoundary();
        });
    }, [updateCanvasBoundary]);
    React.useLayoutEffect(() => {
        const canvasElement = canvasRef?.current;
        const wrapperElement = canvasWrapperRef.current;

        if (!canvasElement || !wrapperElement) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            scheduleCanvasBoundaryUpdate();
        });

        resizeObserver.observe(canvasElement);
        resizeObserver.observe(wrapperElement);

        const handleScroll = () => {
            scheduleCanvasBoundaryUpdate();
        };

        const handleWindowResize = () => {
            scheduleCanvasBoundaryUpdate();
        };

        wrapperElement.addEventListener(
            "scroll",
            handleScroll,
            { passive: true }
        );

        window.addEventListener(
            "resize",
            handleWindowResize
        );

        scheduleCanvasBoundaryUpdate();

        return () => {
            resizeObserver.disconnect();

            wrapperElement.removeEventListener(
                "scroll",
                handleScroll
            );

            window.removeEventListener(
                "resize",
                handleWindowResize
            );

            if (canvasBoundaryFrameRef.current != null) {
                cancelAnimationFrame(
                    canvasBoundaryFrameRef.current
                );

                canvasBoundaryFrameRef.current = null;
            }
        };
    }, [
        canvasRef,
        canvasWrapperRef,
        scheduleCanvasBoundaryUpdate
    ]);
    React.useLayoutEffect(() => {
        let frameId: number;

        const update = () => {
            updateCanvasBoundary();
            frameId = requestAnimationFrame(update);
        };

        frameId = requestAnimationFrame(update);

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [updateCanvasBoundary]);

    const renderOutsideCanvasMask = () => {
        if (!canvasDisplayRect) return null;
        if (openExtandImage.active) return null;

        const wrapper = canvasWrapperRef.current;

        const surfaceWidth =
            wrapper?.scrollWidth ??
            wrapper?.clientWidth ??
            0;

        const surfaceHeight =
            wrapper?.scrollHeight ??
            wrapper?.clientHeight ??
            0;

        const canvasLeft = canvasDisplayRect.left;
        const canvasTop = canvasDisplayRect.top;
        const canvasRight =
            canvasDisplayRect.left +
            canvasDisplayRect.width;

        const canvasBottom =
            canvasDisplayRect.top +
            canvasDisplayRect.height;

        const commonStyle: React.CSSProperties = {
            position: "absolute",
            background: outsideCanvasMaskColor,
            pointerEvents: "none",
            zIndex: 19000
        };

        return (
            <>
                {/* Oberhalb des Canvas */}
                <div
                    aria-hidden="true"
                    style={{
                        ...commonStyle,
                        left: 0,
                        top: 0,
                        width: surfaceWidth,
                        height: Math.max(0, canvasTop)
                    }}
                />

                {/* Links vom Canvas */}
                <div
                    aria-hidden="true"
                    style={{
                        ...commonStyle,
                        left: 0,
                        top: canvasTop,
                        width: Math.max(0, canvasLeft),
                        height: canvasDisplayRect.height
                    }}
                />

                {/* Rechts vom Canvas */}
                <div
                    aria-hidden="true"
                    style={{
                        ...commonStyle,
                        left: canvasRight,
                        top: canvasTop,
                        width: Math.max(
                            0,
                            surfaceWidth - canvasRight
                        ),
                        height: canvasDisplayRect.height
                    }}
                />

                {/* Unterhalb des Canvas */}
                <div
                    aria-hidden="true"
                    style={{
                        ...commonStyle,
                        left: 0,
                        top: canvasBottom,
                        width: surfaceWidth,
                        height: Math.max(
                            0,
                            surfaceHeight - canvasBottom
                        )
                    }}
                />
            </>
        );
    };

    if (openExtandImage.active && (!canvasDisplayRect || canvasDisplayRect.width <= 0 || canvasDisplayRect.height <= 0)) {
        return null;
    }
    const overlayPortal = canvasWrapperRef.current && canvasDisplayRect
    ? createPortal(
        <div
            onMouseDownCapture={(e) => {
                if (!isPickingColor) return;
                if (e.button !== 0) return;

                e.preventDefault();
                e.stopPropagation();

                onPickSurfaceColor?.(e);
            }}
            onDragOver={(e) => {
                e.preventDefault();

                setDragTarget((prev) =>
                    prev === "selected-image-layer" ? prev : "canvas"
                );
            }}
            onDragLeave={(e) => {
                const related = e.relatedTarget as Node | null;

                if (related && e.currentTarget.contains(related)) return;

                setDragTarget(null);
            }}
            onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();

                const rect = canvasWrapperRef.current?.getBoundingClientRect();
                if (!rect) {
                    setDragTarget(null);
                    return;
                }

                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;

                const galleryThumbnailName = e.dataTransfer.getData(
                    "application/x-sengi-gallery-thumbnail"
                );

                if (galleryThumbnailName) {
                    await onDropGalleryImageOnCanvas?.(
                        galleryThumbnailName,
                        x,
                        y
                    );

                    setDragTarget(null);
                    return;
                }

                const file = e.dataTransfer.files?.[0];

                if (!file || !file.type.startsWith("image/")) {
                    setDragTarget(null);
                    return;
                }

                if (dragTarget !== "selected-image-layer") {
                    await onDropImageOnCanvas?.(file, x, y);
                }

                setDragTarget(null);
}}
        >
            {imageLayer.map((layer) => {
                if(layer.hidden){return};
                const style = getLayerDisplayStyle(layer);
                if (!style) return null;
                if (layer.layerID === -1 && layer.type === "image") {
                    return null;
                }

                const isSelected = !layer.hidden && visibleSelectedLayerIDs.includes(layer.layerID);

                const layerStyle: React.CSSProperties = {
                    ...style,
                    transform:
                        rotateMode &&
                        overlayTarget === "layer" &&
                        activeLayerEditId === layer.layerID
                            ? [
                                layer.flipHorizontal ? "scaleX(-1)" : "",
                                layer.flipVertical ? "scaleY(-1)" : "",
                                `rotate(${rotationDraft}deg)`
                            ].filter(Boolean).join(" ")
                            : getLayerCssTransform(layer) || undefined,
                    transformOrigin: "center center",
                    zIndex: isSelected ? 68 : 60,
                    overflow: "visible",
                    border:
                        isSelected && layer.type !== "image" && layer.type !== "ai-variation"
                            ? selectedLayerBorder
                            : (isSelected && dragTarget === "selected-image-layer")
                                ? dragHighlightBorder
                                : undefined,
                    boxShadow:
                        isSelected && layer.type !== "image" && layer.type !== "ai-variation"
                            ? selectedLayerGlow
                            : (isSelected && dragTarget === "selected-image-layer")
                                ? dragHighlightGlow
                                : undefined,
                    boxSizing: isSelected && dragTarget === "selected-image-layer"
                            ? "border-box"
                            : undefined,
                    pointerEvents:isPickingColor
                        ? "none"
                        : isSelectionTool
                            ? selectedAreaTargetLayerId === layer.layerID && visibleSelectedLayer
                                ? "auto"
                                : "none"
                            : "auto"
                };

                if (layer.type === "image" || layer.type === "ai-variation") {
                    const src = layer.imageEdit?.src || layer.imageData?.src;
                    if (!src) return null;

                    const style = layer.imageLayerStyle ?? {};
                    const displayScale = Math.min(scaleX, scaleY);
                    const displayBorderWidth = Math.max(0, (style.borderWidth ?? 0) * displayScale);
                    const displayBorderRadius = getDisplayBorderRadiusPx(
                        style.borderRadius,
                        style.borderRadiusUnit,
                        layer.width * scaleX,
                        layer.height * scaleY,
                        displayScale
                    );

                    const borderRadiusCss = `${displayBorderRadius}px`;
                    const backgroundCss =
                        style.backgroundColor && style.backgroundColor !== "transparent"
                            ? hexToRgbaCss(style.backgroundColor, style.backgroundOpacity ?? 1)
                            : "transparent";

                    const borderCss =
                        (style.borderStyle ?? "none") !== "none" &&
                        (style.borderWidth ?? 0) > 0 &&
                        (style.borderOpacity ?? 0) > 0
                            ? `${displayBorderWidth}px ${style.borderStyle ?? "solid"} ${hexToRgbaCss(style.borderColor ?? "#ffffff", style.borderOpacity ?? 1)}`
                            : "none";

                    const isSelectedSingleImageLayer =
                        visibleSelectedLayerIDs.length === 1 &&
                        visibleSelectedLayer?.layerID === layer.layerID &&
                        (visibleSelectedLayer?.type === "image" || visibleSelectedLayer?.type === "ai-variation");

                    const showDropHighlight = dragTarget === "selected-image-layer" && isSelectedSingleImageLayer;

                    return (
                        <div
                            key={layer.layerID}
                            className="position-absolute"
                            style={{
                                ...layerStyle,
                            }}
                            onMouseDown={(e) => {
                                if (cropMode || rotateMode || openExtandImage.active) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                }

                                if (e.button !== 0) {
                                    e.stopPropagation();
                                    return;
                                }
                                console.log('Imagelayer: ', isSelectionTool)

                                if (isSelectionTool) {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    if (selectedAreaTargetLayerId === layer.layerID) {
                                        beginSelectionDrag(e, layer);
                                    }
                                    return;
                                }

                                e.preventDefault();
                                e.stopPropagation();
                                updatePointerSelection(e, layer);
                            }}
                        >
                            <div
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    position: "relative",
                                    overflow: "hidden",
                                    background: backgroundCss,
                                    borderRadius: borderRadiusCss
                                }}
                            >
                                <img
                                    src={src}
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "fill",
                                        pointerEvents: "none",
                                        opacity: style.imageOpacity ?? 1,
                                        borderRadius: borderRadiusCss
                                    }}
                                />

                                {((style.borderStyle ?? "none") !== "none" &&
                                (style.borderWidth ?? 0) > 0 &&
                                (style.borderOpacity ?? 0) > 0) && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            border: borderCss,
                                            borderRadius: borderRadiusCss,
                                            pointerEvents: "none",
                                            boxSizing: "border-box"
                                        }}
                                    />
                                )}

                                {showDropHighlight && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            inset: -1,
                                            border: dragHighlightBorder,
                                            borderRadius: borderRadiusCss,
                                            boxShadow: dragHighlightGlow,
                                            pointerEvents: "none",
                                            boxSizing: "border-box"
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    );
                }

                if (layer.type === "text" && layer.textData) {
                    const textData = layer.textData;
                    const isEditing = editingTextLayerId === layer.layerID;

                    const isBold = textData.style?.includes("bold") ?? false;
                    const isItalic = textData.style?.includes("italic") ?? false;
                    const isUnderline = textData.style?.includes("underline") ?? false;

                    const displayScale = Math.min(scaleX, scaleY);
                    const rawFontSize = Number((textData.fontSize ?? "16px").replace("px", "")) || 16;
                    const displayFontSize = rawFontSize * displayScale;
                    const displayBorderWidth = Math.max(0, (textData.borderWidth ?? 0) * displayScale);
                    const displayPadding = 6 * displayScale;
                    const textAlign = textData.textAlign ?? "center";
                    const letterSpacing = (textData.letterSpacing ?? 0) * displayScale;
                    const shadowBlur = Math.max(0, (textData.shadowBlur ?? 0) * displayScale);
                    const shadowOffsetX = (textData.shadowOffsetX ?? 0) * displayScale;
                    const shadowOffsetY = (textData.shadowOffsetY ?? 0) * displayScale;
                    const hasShadow = shadowBlur > 0 || shadowOffsetX !== 0 || shadowOffsetY !== 0;
                    const textShadow = hasShadow
                        ? `${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${textData.shadowColor ?? "#000000"}`
                        : "none";
                    const displayBorderRadius = getDisplayBorderRadiusPx(
                        textData.borderRadius,
                        textData.borderRadiusUnit,
                        layer.width * scaleX,
                        layer.height * scaleY,
                        displayScale
                    );
                    const borderRadiusCss = `${displayBorderRadius}px`;
                    const backgroundCss =
                        textData.backgroundColor && textData.backgroundColor !== "transparent"
                            ? hexToRgbaCss(textData.backgroundColor, textData.backgroundOpacity ?? 1)
                            : "transparent";

                    const borderCss =
                        (textData.borderStyle ?? "none") !== "none" &&
                        (textData.borderWidth ?? 0) > 0 &&
                        (textData.borderOpacity ?? 0) > 0
                            ? `${displayBorderWidth}px ${textData.borderStyle ?? "solid"} ${hexToRgbaCss(textData.borderColor ?? "#ffffff", textData.borderOpacity ?? 1)}`
                            : "none";

                    return (
                        <div
                            key={layer.layerID}
                            className="position-absolute"
                            style={layerStyle}
                            onMouseDown={(e) => {
                                if (e.button !== 0) {
                                    e.stopPropagation();
                                    return;
                                }

                                if (isEditing) {
                                    e.stopPropagation();
                                    return;
                                }

                                if (isSelectionTool) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (selectedAreaTargetLayerId === layer.layerID) {
                                        beginSelectionDrag(e, layer);
                                    }
                                    return;
                                }

                                e.preventDefault();
                                e.stopPropagation();
                                updatePointerSelection(e, layer);
                            }}
                            onDoubleClick={(e) => {
                                if (layer.type === "text") {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    onStartTextEdit?.(layer);
                                }
                            }}
                        >
                            {isEditing && !(layer.imageData && layer.imageData.src)
                            ? (
                                <textarea
                                    value={editingTextValue}
                                    autoFocus
                                    onChange={(e) => onChangeEditingText?.(e.target.value)}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onDoubleClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Escape") {
                                            e.preventDefault();
                                            onCommitTextEdit?.();
                                        }

                                        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
                                            e.stopPropagation();
                                        }
                                    }}
                                    onBlur={() => {
                                        onCommitTextEdit?.();
                                    }}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        resize: "none",
                                        outline: "none",
                                        pointerEvents: "auto",
                                        caretColor: textData.fontColor ?? "#ffffff",
                                        boxSizing: "border-box",
                                        background: backgroundCss,
                                        color: hexToRgbaCss(textData.fontColor ?? "#ffffff", textData.textOpacity ?? 1),
                                        fontFamily: textData.fontFamily ?? "Arial",
                                        fontSize: `${displayFontSize}px`,
                                        fontWeight: isBold ? "bold" : "normal",
                                        fontStyle: isItalic ? "italic" : "normal",
                                        textDecoration: isUnderline ? "underline" : "none",
                                        border: borderCss,
                                        padding: `${displayPadding}px`,
                                        overflow: "hidden",
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                        textAlign,
                                        letterSpacing: `${letterSpacing}px`,
                                        textShadow,
                                        borderRadius: borderRadiusCss,
                                        lineHeight: 1.2,
                                        paddingTop: `${displayPadding}px`,
                                        backgroundClip: "padding-box",
                                        userSelect: "text",
                                        WebkitUserSelect: "text"
                                    }}
                                />
                            ) 
                            : isSelected && (layer.imageData && layer.imageData.src) // for hover preview in text layer
                                ? (
                                    <img
                                        src={layer.imageData.src}
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "fill",
                                            pointerEvents: "none",
                                            opacity:  1
                                        }}
                                    />
                                )
                                : (
                                    <div
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            boxSizing: "border-box",
                                            position: "relative",
                                            overflow: "hidden",
                                            border: borderCss,
                                            background: backgroundCss,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: `${displayPadding}px`,
                                            borderRadius: borderRadiusCss
                                        }}
                                    >
                                        <div
                                            style={{
                                                color: textData.fontColor ?? "#ffffff",
                                                fontFamily: textData.fontFamily ?? "Arial",
                                                fontSize: `${displayFontSize}px`,
                                                fontWeight: isBold ? "bold" : "normal",
                                                fontStyle: isItalic ? "italic" : "normal",
                                                textDecoration: isUnderline ? "underline" : "none",
                                                opacity: textData.textOpacity ?? 1,
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                overflowWrap: "anywhere",
                                                lineHeight: 1.2,
                                                width: "100%",
                                                maxWidth: "100%",
                                                textAlign,
                                                letterSpacing: `${letterSpacing}px`,
                                                textShadow,
                                                pointerEvents: "none"
                                            }}
                                        >
                                            {textData.text}
                                        </div>
                                    </div>
                                )}
                        </div>
                    );
                }

                if (layer.type === "drawings") {
                    const drawingSrc = layer.drawingData?.rasterSrc;

                    return (
                        <div
                            key={layer.layerID}
                            className="position-absolute"
                            style={{
                                ...layerStyle,
                            }}
                            onPointerDown={(e) => {
                                if (e.button !== 0) {
                                    e.stopPropagation();
                                    return;
                                }
                                if (cropMode || rotateMode || openExtandImage.active) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                } 
                                if (isSelectionTool) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (selectedAreaTargetLayerId === layer.layerID) {
                                        beginSelectionDrag(e, layer);
                                    }
                                    return;
                                }

                                const drawingIsActiveOnThisLayer =
                                    !!selectedDrawingTool &&
                                    isSelected &&
                                    activeToolkitTool !== "hand" &&
                                    !isSelectionTool &&
                                    !isPickingColor;

                                if (drawingIsActiveOnThisLayer) {
                                    beginDrawingStroke(e);
                                    return;
                                }

                                if (activeToolkitTool === "hand") {
                                    beginLayerTransform(e, layer, "move");
                                    return;
                                }

                                if (activeToolkitTool === "pointer") {
                                    updatePointerSelection(e, layer);
                                    return;
                                }
                            }}
                        >
                            {drawingSrc && !(layer.imageData && layer.imageData.src) 
                                ? (
                                    <img
                                        src={drawingSrc}
                                        alt=""
                                        draggable={false}
                                        style={{ width: "100%", height: "100%", pointerEvents: "none",imageRendering: "auto" }}
                                    />
                                ) 
                                : isSelected && (layer.imageData && layer.imageData.src) // for hover preview in drawing layer
                                ? (
                                    <img
                                        src={layer.imageData.src}
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "fill",
                                            pointerEvents: "none",
                                            opacity:  1
                                        }}
                                    />
                                )
                            : null
                            }
                            
                            {isSelected && shapePreview && selectedDrawingTool.startsWith("shape-") ? (
                                <svg
                                    width="100%"
                                    height="100%"
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        pointerEvents: "none",
                                        overflow: "visible"
                                    }}
                                    viewBox={`0 0 ${layer.width} ${layer.height}`}
                                >
                                    <g opacity={shapePreview.opacity}>
                                        {getPreviewHardnessLayers(
                                            shapePreview.size,
                                            shapePreview.hardness
                                        ).map((shapeLayer, index) => (
                                            <path
                                                key={`shape-soft-${index}`}
                                                d={
                                                    shapePreview.points.length
                                                        ? `M ${shapePreview.points.map((p) => `${p.x} ${p.y}`).join(" L ")}`
                                                        : ""
                                                }
                                                fill="none"
                                                stroke={shapePreview.color}
                                                strokeWidth={shapeLayer.width}
                                                strokeOpacity={shapeLayer.opacity}
                                                strokeLinejoin="round"
                                                strokeLinecap="round"
                                            />
                                        ))}

                                        <path
                                            d={
                                                shapePreview.points.length
                                                    ? `M ${shapePreview.points.map((p) => `${p.x} ${p.y}`).join(" L ")}`
                                                    : ""
                                            }
                                            fill="none"
                                            stroke={shapePreview.color}
                                            strokeWidth={Math.max(1, shapePreview.size * (0.12 + shapePreview.hardness * 0.86))}
                                            strokeOpacity={0.45 + shapePreview.hardness * 0.55}
                                            strokeLinejoin="round"
                                            strokeLinecap="round"
                                        />
                                    </g>
                                </svg>
                            ) : null}
                            {isSelected && drawingStrokePreview && !selectedDrawingTool.startsWith("shape-") ? (
                                <svg
                                    width="100%"
                                    height="100%"
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        pointerEvents: "none",
                                        overflow: "visible"
                                    }}
                                    viewBox={`0 0 ${layer.width} ${layer.height}`}
                                >
                                <g opacity={drawingStrokePreview.opacity}>
                                        {getPreviewHardnessLayers(
                                            drawingStrokePreview.size,
                                            drawingStrokePreview.hardness
                                        ).map((brushLayer, index) => (
                                            <polyline
                                                key={`${index}-${brushLayer.width}`}
                                                points={drawingStrokePreview.points.map(point => `${point.x},${point.y}`).join(" ")}
                                                fill="none"
                                                stroke={drawingStrokePreview.tool === "eraser" ? "#888" : drawingStrokePreview.color}
                                                strokeWidth={brushLayer.width}
                                                strokeOpacity={brushLayer.opacity}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        ))}

                                        <polyline
                                            points={drawingStrokePreview.points.map(point => `${point.x},${point.y}`).join(" ")}
                                            fill="none"
                                            stroke={drawingStrokePreview.tool === "eraser" ? "#888" : drawingStrokePreview.color}
                                            strokeWidth={Math.max(1, drawingStrokePreview.size * (0.12 + drawingStrokePreview.hardness * 0.86))}
                                            strokeOpacity={0.45 + drawingStrokePreview.hardness * 0.55}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                </svg>
                            ) : null}
                        </div>
                    );
                }

                return null;
            })}
            {renderOutsideCanvasMask()}
            {drawingCursorPreview?.visible && drawingCursorDisplayStyle ? (
                <div style={drawingCursorDisplayStyle} />
            ) : null}
            {canvasDisplayRect &&  (
                <div
                    className="position-absolute"
                    style={{
                        left: canvasDisplayRect.left,
                        top: canvasDisplayRect.top,
                        width: canvasDisplayRect.width,
                        height: canvasDisplayRect.height,
                        zIndex: 5,
                        background: "transparent",
                        cursor: isSelectionTool
                            ? "crosshair"
                            : activeToolkitTool === "pointer"
                                ? "default"
                                : "auto",
                        pointerEvents:
                            activeToolkitTool === "hand" ||
                            isPickingColor ||
                            isDrawingActionTool
                                ? "none"
                                : (visibleSelectedLayerIDs.length > 0 &&
                                activeToolkitTool !== null && ["select-rect", "select-oval", "select-free"].includes(activeToolkitTool))
                                    ? "none"
                                    : "auto"
                    }}
                    onMouseDown={(e) => {
                        if (cropMode || rotateMode || openExtandImage.active) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }                        
                        if (e.button !== 0) return;
                        if (isDrawingActionTool) {
                            return;
                        }
                        if (activeToolkitTool === "pointer") {
                            onSelectedLayerIDsChange?.([]);
                            onSelectedLayerChange?.(null);
                            setLayerAreaSelection(null);
                            beginPointerMarqueeSelection(e);
                            return;
                        }

                        if (isSelectionTool) {
                            if (visibleSelectedLayerIDs.length === 0) {
                                beginSelectionDrag(e,null);
                            }
                            return;
                        }

                        if (
                            visibleSelectedLayerIDs.length === 1 &&
                            activeToolkitTool && !["pointer", "hand", "select-rect", "select-oval", "select-free"].includes(activeToolkitTool)
                        ) {
                            beginDrawingStroke(e);
                            return;
                        }
                    }}
                />
            )}

            {marqueeSelectionPreview && (() => {
                const rect = normalizeRect(
                    marqueeSelectionPreview.startX,
                    marqueeSelectionPreview.startY,
                    marqueeSelectionPreview.currentX,
                    marqueeSelectionPreview.currentY
                );
                const style = getLayerDisplayStyle({
                    layerID: -1,
                    type: "drawings",
                    orderNumber: "0",
                    position: { x: rect.x, y: rect.y },
                    width: rect.w,
                    height: rect.h
                } as LayerProps);

                if (!style) return null;

                return (
                    <div
                        className="position-absolute"
                        style={{
                            ...style,
                            zIndex: 70,
                            border: "1px dashed #9381FF",
                            background: "rgba(147,129,255,0.12)",
                            pointerEvents: "none"
                        }}
                    />
                );
            })()}
            {selectionPreview && (() => {
                const style = getLayerDisplayStyle({
                    layerID: -1,
                    type: "drawings",
                    orderNumber: "0",
                    position: {
                        x: Math.min(selectionPreview.startX, selectionPreview.currentX),
                        y: Math.min(selectionPreview.startY, selectionPreview.currentY)
                    },
                    width: Math.abs(selectionPreview.currentX - selectionPreview.startX),
                    height: Math.abs(selectionPreview.currentY - selectionPreview.startY)
                } as LayerProps);

                if (selectionPreview.mode === "rect" || selectionPreview.mode === "oval") {
                    if (!style) return null;

                    return (
                        <div
                            className={`position-absolute ${selectionPreview.mode === "oval" ? "rounded-circle" : ""}`}
                            style={{
                                ...style,
                                zIndex: 71,
                                border: `2px dashed ${selectionStroke}`,
                                background: selectionFill,
                                pointerEvents: "none"
                            }}
                        />
                    );
                }

                const points = selectionPreview.points;
                if (!points.length || !canvasDisplayRect || !canvasRef?.current) return null;

                const canvas = canvasRef.current as HTMLCanvasElement;
                const scaleX = canvasDisplayRect.width / canvas.width;
                const scaleY = canvasDisplayRect.height / canvas.height;

                const path = points
                    .map((p, index) => {
                        const x = canvasDisplayRect.left + p.x * scaleX;
                        const y = canvasDisplayRect.top + p.y * scaleY;
                        return `${index === 0 ? "M" : "L"} ${x} ${y}`;
                    })
                    .join(" ");

                return (
                    <svg
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{ zIndex: 71, pointerEvents: "none" }}
                    >
                        <path
                            d={`${path} Z`}
                            fill={selectionFill}
                            stroke={selectionStroke}
                            strokeDasharray="6 4"
                            strokeWidth={2}
                        />
                    </svg>
                );
            })()}
            {layerAreaSelection && (() => {
                const style = getLayerDisplayStyle({
                    layerID: -1,
                    type: "drawings",
                    orderNumber: "0",
                    position: { x: layerAreaSelection.x, y: layerAreaSelection.y },
                    width: layerAreaSelection.w,
                    height: layerAreaSelection.h
                } as LayerProps);

                if (!style) return null;

                if (layerAreaSelection.mode === "rect") {
                    return (
                        <div
                            className="position-absolute"
                            style={{
                                ...style,
                                zIndex: 72,
                                border: "1px dashed #00D1FF",
                                background: "rgba(0,209,255,0.12)",
                                pointerEvents: "none"
                            }}
                        />
                    );
                }

                if (layerAreaSelection.mode === "oval") {
                    return (
                        <div
                            className="position-absolute rounded-circle"
                            style={{
                                ...style,
                                zIndex: 72,
                                border: "1px dashed #00D1FF",
                                background: "rgba(0,209,255,0.12)",
                                pointerEvents: "none"
                            }}
                        />
                    );
                }

                const points = layerAreaSelection.points;
                if (!points.length || !canvasDisplayRect || !canvasRef?.current) return null;

                const canvas = canvasRef.current as HTMLCanvasElement;
                const scaleX = canvasDisplayRect.width / canvas.width;
                const scaleY = canvasDisplayRect.height / canvas.height;

                const toPath = (pathPoints: Point[]) =>
                    pathPoints
                        .map((p, index) => {
                            const x = canvasDisplayRect.left + p.x * scaleX;
                            const y = canvasDisplayRect.top + p.y * scaleY;
                            return `${index === 0 ? "M" : "L"} ${x} ${y}`;
                        })
                        .join(" ") + " Z";

                const path = [
                    toPath(points),
                ].join(" ");

                return (
                    <svg
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{ zIndex: 72, pointerEvents: "none" }}
                    >
                        <path
                            d={`${path}`}
                            fill="rgba(0,209,255,0.12)"
                            stroke="#00D1FF"
                            strokeDasharray="6 4"
                        />
                    </svg>
                );
            })()}
            {cropMode && activeOverlayBaseRect && (
                <>
                    {/* Abdunkelung über der Canvas */}
                    <div
                        className="position-absolute"
                        style={{
                            left: canvasDisplayRect.left,
                            top: canvasDisplayRect.top,
                            width: canvasDisplayRect.width,
                            height: canvasDisplayRect.height,
                            background: "rgba(0,0,0,0.35)",
                            zIndex: 1100,
                            pointerEvents: "none"
                        }}
                    />

                    {/* Crop-Box */}
                    <div
                    className="position-fixed"
                    style={{
                        top: "1.2em",
                        right: "1.5em",
                        pointerEvents: "auto",
                        zIndex: 1200
                    }}
                >
                    <div className="btn-group shadow">
                        <button
                            className={`btn btn-sm ${effectiveTheme=== 'dark' ? 'btn-light' : 'btn-sengi'}`}
                            type="button"
                            title={t('actions.save')}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCropSave();
                            }}
                        >
                            {t('actions.save')}
                        </button>

                        <button
                            className={`btn btn-sm ${effectiveTheme=== 'dark' ? 'btn-outline-light' : 'btn-outline-sengi'}`}
                            type="button"
                            title={t('actions.cancel')}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                closeOverlays();
                            }}
                        >
                            {t('actions.cancel')}
                        </button>
                    </div>
                </div>
                {cropMode && activeOverlayBaseRect && overlayTarget === "layer" && activeEditedLayer && (
    <div
        className="position-absolute"
        style={{
            left: activeOverlayBaseRect.left,
            top: activeOverlayBaseRect.top,
            width: activeOverlayBaseRect.width,
            height: activeOverlayBaseRect.height,
            zIndex: 1101,
            transform: getLayerCssTransform(activeEditedLayer) || undefined,
            transformOrigin: "center center",
            pointerEvents: "none"
        }}
    >
        <div
            className="position-absolute"
            style={{
                left: overlayRect.x * activeOverlayBaseRect.width,
                top: overlayRect.y * activeOverlayBaseRect.height,
                width: overlayRect.w * activeOverlayBaseRect.width,
                height: overlayRect.h * activeOverlayBaseRect.height,
                border: `2px dashed ${SengiColors.sng_lavendel}`,
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.18)",
                background: "rgba(255,255,255,0.06)",
                cursor: "move",
                boxSizing: "border-box",
                pointerEvents: "auto"
            }}
            onMouseDown={(e) => beginCropDrag(e, "move")}
        >
            {(["nw", "ne", "sw", "se"] as OverlayCorner[]).map((visualCorner) => {
                const localCorner = getLocalCornerForVisualCorner(activeEditedLayer, visualCorner);

                const style: React.CSSProperties = {
                    position: "absolute",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: `2px solid ${SengiColors.sng_lavendel}`,
                    background: "#fff",
                    cursor:
                        visualCorner === "nw" || visualCorner === "se"
                            ? "nwse-resize"
                            : "nesw-resize"
                };

                if (localCorner.includes("n")) style.top = "-6px";
                if (localCorner.includes("s")) style.bottom = "-6px";
                if (localCorner.includes("w")) style.left = "-6px";
                if (localCorner.includes("e")) style.right = "-6px";

                return (
                    <div
                        key={visualCorner}
                        style={style}
                        onMouseDown={(e) => beginCropDrag(e, visualCorner)}
                    />
                );
            })}
        </div>
    </div>
)}
                {cropMode && activeOverlayBaseRect && overlayTarget !== "layer" && (<div
                        className="position-absolute"
                        style={{
                            ...overlayStyle,
                            zIndex: 1101,
                            border: `2px dashed ${SengiColors.sng_lavendel}`,
                            boxShadow: "0 0 0 9999px rgba(0,0,0,0.18)",
                            background: "rgba(255,255,255,0.06)",
                            cursor: "move",
                            boxSizing: "border-box",
                            transform:
                                cropLayerTransform
                                    ? cropLayerTransform
                                    : undefined,
                            transformOrigin: "center center"
                        }}
                        onMouseDown={(e) => beginCropDrag(e, "move")}
                    >
                        {(["nw", "ne", "sw", "se"] as OverlayCorner[]).map((visualCorner) => {
                            const localCorner = visualCorner;

                            const style: React.CSSProperties = {
                                position: "absolute",
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                border: `2px solid ${SengiColors.sng_lavendel}`,
                                background: "#fff",
                                transform: cropOverlayCounterTransform || undefined,
                                transformOrigin: "center center",
                                cursor:
                                    visualCorner === "nw" || visualCorner === "se"
                                        ? "nwse-resize"
                                        : "nesw-resize"
                            };

                            if (localCorner.includes("n")) style.top = "-6px";
                            if (localCorner.includes("s")) style.bottom = "-6px";
                            if (localCorner.includes("w")) style.left = "-6px";
                            if (localCorner.includes("e")) style.right = "-6px";

                            return (
                                <div
                                    key={visualCorner}
                                    className="position-absolute"
                                    style={style}
                                    onMouseDown={(e) => beginCropDrag(e, visualCorner)}
                                />
                            );
                        })}
                    </div>
                )}
                </>
            )}

            {rotateMode && (
                <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ zIndex: 1200 }}
                    onMouseDown={() => setContextMenu((prev: ContextProps) => ({ ...prev, visible: false }))}
                >
                    <div className="position-absolute" style={{ top: 12, right: 12 }}>
                        <div className="btn-group">
                            <button className={`btn btn-sm ${effectiveTheme=== 'dark' ? 'btn-light' : 'btn-sengi'}`} title={t('actions.save')} onClick={() => handleRotateSave()}>
                                {t('actions.save')} ({normalizeRotation(rotationDraft)}°)
                            </button>
                            <button className={`btn btn-sm ${effectiveTheme=== 'dark' ? 'btn-outline-light' : 'btn-outline-sengi'}`} title={t('actions.calcel')} onClick={() => closeOverlays()}>
                                {t('actions.cancel')}
                            </button>
                        </div>
                    </div>

                    <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ userSelect: 'none' }}
                    >
                        <div
                            className="p-3 rounded-circle"
                            style={{ background: 'rgba(0, 0, 0, 0.2)', cursor: 'grab', border:'1px solid rgba(147 129 255,0.4)'}}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onIsRotating?.(true);
                            }}
                        >
                            <FontAwesomeIcon icon={faRotate} size="2x" className="text-light" />
                        </div>
                        <div className="mt-2 p-2 rounded h5" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55)', color: '#fff' ,filter: 'drop-shadow(2px 2px 6px rgba(0, 0, 0, 0.8))'}}>
                            {t('actions.rotateInstruction')}
                        </div>
                    </div>
                </div>
            )}
            {!cropMode && !rotateMode && !openExtandImage.active && imageLayer.map((layer) => {
                const style = getLayerDisplayStyle(layer);
                if (!style || layer.hidden) return null;

                const isSelected = !layer.hidden && visibleSelectedLayerIDs.includes(layer.layerID);
                const isPrimarySelected = visibleSelectedLayer?.layerID === layer.layerID;
                const layerReceivesSelectionEvents = isSelectionTool && selectedAreaTargetLayerId === layer.layerID;
                const isSelectedSingleImageLayer = visibleSelectedLayerIDs.length === 1 && visibleSelectedLayer?.layerID === layer.layerID &&(visibleSelectedLayer?.type === "image" || visibleSelectedLayer?.type === "ai-variation");
                
                const transformedFrameStyle: React.CSSProperties = {
                    left: style.left,
                    top: style.top,
                    width: style.width,
                    height: style.height,
                    transform: getLayerCssTransform(layer) || undefined,
                    transformOrigin: "center center",
                    overflow: "visible"
                };
                const drawingCursorValue: string =
                    getDrawingCursorValue(
                        selectedDrawingTool,
                        drawingCursorPreview?.color,
                        drawingCursorHotspot
                    ) ?? "pointer";

                return (
                    <div
                        key={layer.layerID}
                        className="position-absolute"
                        style={{
                            ...transformedFrameStyle,
                            border: isSelected
                                ? `2px solid ${SengiColors.sng_veilchen}`
                                : "none",
                            background: isPrimarySelected
                                ? "rgba(228, 204, 255, 0.05)"
                                : "transparent",
                            boxShadow: isPrimarySelected
                                ? "0 0 0 2px rgba(184,184,255,0.18), 0 6px 18px rgba(147,129,255,0.18)"
                                : "none",
                            boxSizing: "border-box",
                            zIndex: isSelected ? 80 : 70,
                            pointerEvents: isPickingColor
                                ? "none"
                                : isSelectionTool
                                    ? layerReceivesSelectionEvents
                                        ? "auto"
                                        : "none"
                                    : "auto",
                            cursor: isPickingColor
                                ? "crosshair"
                                : isSelectionTool
                                    ? "crosshair"
                                    : layer.type === "drawings" && isSelected && isDrawingActionTool
                                        ? drawingCursorValue
                                        : activeToolkitTool === "hand" && isSelected
                                            ? "move"
                                            : activeToolkitTool === "pointer"
                                                ? "pointer"
                                                : "default"
                        }}
                        onMouseDown={(e) => {
                            if (e.button !== 0) {
                                e.stopPropagation();
                                return;
                            }
                            if (isPickingColor) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                            }
                            e.stopPropagation();
                            e.preventDefault();

                            const drawingIsActiveOnThisLayer =
                                layer.type === "drawings" &&
                                isSelected &&
                                isDrawingActionTool &&
                                activeToolkitTool !== "hand" &&
                                !isSelectionTool &&
                                !isPickingColor;

                            if (drawingIsActiveOnThisLayer) {
                                beginDrawingStroke(e);
                                return;
                            }

                            if (isSelectionTool) {
                                e.preventDefault();
                                e.stopPropagation();

                                if (selectedAreaTargetLayerId === layer.layerID) {
                                    beginSelectionDrag(e, layer);
                                }
                                return;
                            }

                            if (activeToolkitTool === "pointer") {
                                updatePointerSelection(e, layer);
                                return;
                            }
                        }}
                        onPointerDown={(e) => {
                            if (e.button !== 0) return;
                            if ( activeToolkitTool !== "hand" || !isSelected || isPickingColor)return;

                            e.preventDefault();
                            e.stopPropagation();
                            beginLayerTransform(e,layer,"move");
                        }}
                        onDragOver={(e) => {
                            if (!isSelectedSingleImageLayer) {
                                return;
                            }

                            e.preventDefault();
                            e.stopPropagation();
                            setDragTarget("selected-image-layer");
                        }}
                        onDragLeave={(e) => {
                            e.stopPropagation();

                            const related = e.relatedTarget as Node | null;
                            if (related && e.currentTarget.contains(related)) return;

                            setDragTarget("canvas");
                        }}
                        onDrop={async (e) => {
                            if (!isSelectedSingleImageLayer) {
                                return;
                            }

                            e.preventDefault();
                            e.stopPropagation();

                            const galleryThumbnailName = e.dataTransfer.getData("application/x-sengi-gallery-thumbnail");

                            if (galleryThumbnailName) {
                                await onDropGalleryImageOnSelectedImageLayer?.(galleryThumbnailName);
                                setDragTarget(null);
                                return;
                            }

                            const file = e.dataTransfer.files?.[0];
                            setDragTarget(null);

                            if (!file || !file.type.startsWith("image/")) return;

                            await onDropImageOnSelectedImageLayer?.(file);
                        }}
                        onDoubleClick={(e) => {
                            if (visibleSelectedLayerIDs.length === 1 && visibleSelectedLayer?.layerID === layer.layerID && layer.type === "text") {
                                e.stopPropagation();
                                e.preventDefault();
                                onStartTextEdit?.(layer);
                            }
                        }}
                    >
                        {isPrimarySelected && visibleSelectedLayerIDs.length === 1 && (
                            <>
                                {([
                                    { k: "nw", s: { left: -6, top: -6, cursor: "nwse-resize" } },
                                    { k: "ne", s: { right: -6, top: -6, cursor: "nesw-resize" } },
                                    { k: "sw", s: { left: -6, bottom: -6, cursor: "nesw-resize" } },
                                    { k: "se", s: { right: -6, bottom: -6, cursor: "nwse-resize" } }
                                ] as const).map((h) => (
                                    <div
                                        key={h.k}
                                        className="position-absolute rounded-circle"
                                        style={{
                                            width: 12,
                                            height: 12,
                                            background: "linear-gradient(135deg, #E4CCFF 0%, #B8B8FF 55%, #9381FF 100%)",
                                            border: "2px solid rgba(255,255,255,0.95)",
                                            boxShadow: "0 2px 8px rgba(147,129,255,0.28)",
                                            ...h.s,
                                             touchAction: "none",
                                            userSelect: "none"
                                        }}
                                        onPointerDown={(e) => beginLayerTransform(e, layer, h.k)}
                                        onDragOver={(e) => {
                                            if (!isSelectedSingleImageLayer) {
                                                return;
                                            }

                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDragTarget("selected-image-layer");
                                        }}
                                        onDragLeave={(e) => {
                                            e.stopPropagation();
                                            setDragTarget("canvas");
                                        }}
                                        onDrop={async (e) => {
                                            if (!isSelectedSingleImageLayer) {
                                                return;
                                            }

                                            e.preventDefault();
                                            e.stopPropagation();

                                            const galleryThumbnailName = e.dataTransfer.getData(
                                                "application/x-sengi-gallery-thumbnail"
                                            );

                                            if (galleryThumbnailName) {
                                                await onDropGalleryImageOnSelectedImageLayer?.(galleryThumbnailName);
                                                setDragTarget(null);
                                                return;
                                            }

                                            const file = e.dataTransfer.files?.[0];
                                            setDragTarget(null);

                                            if (!file || !file.type.startsWith("image/")) return;

                                            await onDropImageOnSelectedImageLayer?.(file);
                                        }}
                                        onDoubleClick={(e) => {
                                            if (visibleSelectedLayerIDs.length === 1 && visibleSelectedLayer?.layerID === layer.layerID && layer.type === "text") {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                onStartTextEdit?.(layer);
                                            }
                                        }}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                );
            })}
            {dragTarget === "canvas" && canvasDisplayRect && (
                <div
                    style={{
                        position: "absolute",
                        left: canvasDisplayRect.left,
                        top: canvasDisplayRect.top,
                        width: canvasDisplayRect.width,
                        height: canvasDisplayRect.height,
                        border: dragHighlightBorder,
                        boxShadow: dragHighlightGlow,
                        boxSizing: "border-box",
                        pointerEvents: "none",
                        zIndex: 50
                    }}
                />
            )}
            {showCanvasBorder && (
                    <div
                        ref={canvasBoundaryRef}
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            left: 0,
                            top: 0,

                            width: 0,
                            height: 0,

                            border:
                                effectiveTheme === "dark"
                                    ? `2px solid ${SengiColors.sng_pearl}`
                                    : `2px solid ${SengiColors.sng_anthracite}`,
                            boxShadow: effectiveTheme === "dark"
                                ? "0 4px 18px rgba(255,255,255,0.22)"
                                : "0 4px 18px rgba(0,0,0,0.30)",
                            boxSizing: "border-box",
                            pointerEvents: "none",
                            zIndex: 20000,
                            willChange: "transform, width, height"
                        }}
                    >
                    <div
                        ref={canvasBoundaryRef}
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            left: 0,
                            top: 0,

                            width: 0,
                            height: 0,

                            border:
                                effectiveTheme === "dark"
                                    ? `2px solid ${SengiColors.sng_anthracite}`
                                    : `2px solid ${SengiColors.sng_pearl}`,
                            boxSizing: "border-box",
                            pointerEvents: "none",
                            zIndex: 20000,
                            willChange: "transform, width, height"
                        }}
                    />
                    </div>
            )}
            
            {renderExtendOverlay()}
        </div>,
        canvasWrapperRef.current
    )
    : null;
    return overlayPortal;
};
